import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import * as adminApi from "@/api/admin";
import type { LandingPageDetail, LandingPageSection } from "@/api/admin";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { QuestionnaireForm } from "./QuestionnaireForm";
import { StepsSection } from "./StepsSection";
import { CardsSection } from "./CardsSection";
import paduLogo from "@/assets/padu_new_logo_sign.png";

function RevealSection({ children }: { children: React.ReactNode }) {
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
      }}
    >
      {children}
    </div>
  );
}

function SectionBlock({ section, slug }: { section: LandingPageSection; slug: string }) {
  if (section.section_type === "hero") {
    return (
      <div className="flex flex-col md:flex-row items-center gap-8">
        <div className="flex-1 space-y-4 text-center md:text-left">
          {section.heading && (
            <h2 className="text-2xl md:text-3xl font-bold text-white whitespace-pre-line">
              {section.heading}
            </h2>
          )}
          {section.body && (
            <p className="text-white/60 text-base md:text-lg leading-relaxed whitespace-pre-line">
              {section.body}
            </p>
          )}
          {section.button_text && section.button_url && (
            <a
              href={section.button_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#00d9d9] hover:scale-[1.02] text-black font-bold text-sm rounded-full px-8 py-3.5 transition-transform duration-200"
            >
              {section.button_text}
            </a>
          )}
        </div>
        {section.image_url && (
          <div className="flex-1">
            <img
              src={section.image_url}
              alt={section.heading || ""}
              className="w-full rounded-2xl"
            />
          </div>
        )}
      </div>
    );
  }

  if (section.section_type === "steps") {
    return <StepsSection section={section} />;
  }

  if (section.section_type === "cards") {
    return <CardsSection section={section} />;
  }

  if (section.section_type === "text") {
    return (
      <div className="space-y-3 text-center">
        {section.heading && (
          <h2 className="text-2xl md:text-3xl font-bold text-white">{section.heading}</h2>
        )}
        {section.body && (
          <p className="text-white/60 text-base md:text-lg leading-relaxed whitespace-pre-line">
            {section.body}
          </p>
        )}
      </div>
    );
  }

  if (section.section_type === "image") {
    return (
      <div className="space-y-3">
        {section.heading && (
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center">
            {section.heading}
          </h2>
        )}
        {section.image_url && (
          <img
            src={section.image_url}
            alt={section.heading || ""}
            className="w-full rounded-2xl border border-white/10"
          />
        )}
      </div>
    );
  }

  // questionnaire
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5">
      {section.heading && (
        <h2 className="text-xl md:text-2xl font-bold text-white text-center">
          {section.heading}
        </h2>
      )}
      <QuestionnaireForm slug={slug} section={section} />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <div className="max-w-desktop mx-auto px-4 py-20 space-y-10 animate-pulse">
        <div className="h-8 w-2/3 bg-white/10 rounded mx-auto" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-white/10 rounded" />
          <div className="h-4 w-5/6 bg-white/10 rounded mx-auto" />
        </div>
        <div className="h-64 w-full bg-white/5 rounded-2xl" />
      </div>
    </div>
  );
}

export function LandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<LandingPageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    adminApi
      .getPublicLandingPage(slug)
      .then(setPage)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <LoadingSkeleton />;

  if (notFound || !page) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-4">
        <div className="text-center">
          <img src={paduLogo} alt="PADU" className="w-12 h-12 mx-auto mb-4 opacity-60" />
          <p className="text-lg font-semibold text-white">Η σελίδα δεν είναι διαθέσιμη.</p>
          <p className="text-sm text-white/40 mt-1">
            Μπορεί να έχει αποσυρθεί ή ο σύνδεσμος να μην είναι σωστός.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a] dot-grid">
      <div className="max-w-desktop mx-auto px-4 py-16 md:py-24 space-y-14">

        {page.sections.map((section) => (
          <RevealSection key={section.id}>
            <SectionBlock section={section} slug={slug!} />
          </RevealSection>
        ))}
      </div>
    </div>
  );
}
